import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CandleSticks from '../../../../components/Charts/CandleSticks';
import { PriceChartApiResponse } from '../../Charts';
import {
  Bot,
  CandleType,
  TimeFrame,
  TransactionDetailsApiResponse,
} from '../../types';
import { ComponentDimensions } from '../../../../types/Layout';
import ChartOptions from './ChartOptions';

import './index.css';

import { Transaction } from '../../../Overview/types';
import LoadingIcons from 'react-loading-icons';
import { CandleSticksLatestOrder } from '../../Charts/types';

interface Props {
  candles: PriceChartApiResponse | undefined;
  bot: Bot;
  timeFrame: TimeFrame;
  transactions: TransactionDetailsApiResponse | undefined;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  multiOrderCandles?: PriceChartApiResponse | undefined;
  bundledTransactions: Record<string, Transaction[]>;
  realCompactTransactions: Transaction[] | undefined;
  isCompareToProfitCalcs: boolean;
  setIsCompareToProfitCalcs: React.Dispatch<React.SetStateAction<boolean>>;
  compactProfitCalcsTransactions: Transaction[] | undefined;
  profitCalcsTransactions: Transaction[] | undefined;
  candleSticksLatestOrder?: CandleSticksLatestOrder;
}

/**
 * We want to show real transactions and profit calc transactions on candles
 * I want to be able to zoomIn on shorter and multi
 *
 * When I press a transaction,
 * I want to see a zoomedIn state from both real and profit calcs transaction
 * (What if one is just completely missing?)
 *
 * What about the different profit calcs from real transactions
 * 1.Take the last one and start at the timestamp of the previous transaction
 * 2. Make sure we do a profit calc for each different complex params found in the transactions
 *
 * We need:
 * The candles to show
 * transaction dots of the real transactions
 * transaction dots of profit calc transactions
 * Feature to handle the press on the transaction
 * Hover shows the transaction info
 */

const TransactionCandles = (props: Props) => {
  const {
    bot,
    timeFrame,
    candles,
    transactions,
    multiOrderCandles,
    bundledTransactions,
    realCompactTransactions,
    isCompareToProfitCalcs,
    compactProfitCalcsTransactions,
    profitCalcsTransactions,
    isLoading,
    setIsLoading,
    setIsCompareToProfitCalcs,
    candleSticksLatestOrder,
  } = props;

  /** TODO: feature: control:
   * Every new multiCandle, check the inSync of the real bot and profit calcs */

  /** ============================================== STATES ============================================== */
  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  const firstTransactionStartTimestamp = useMemo(() => {
    if (!transactions?.compactTransactions?.length) {
      return 0;
    }

    const firstTransaction =
      transactions.compactTransactions[
        transactions.compactTransactions.length - 1
      ];
    return firstTransaction.transactionTimestamp;
  }, [transactions?.compactTransactions]);

  const filteredCandles = useMemo(
    () => ({
      normal:
        candles?.normal.filter(
          (c) =>
            c.startTimestamp >= firstTransactionStartTimestamp ||
            (c.startTimestamp <= firstTransactionStartTimestamp &&
              firstTransactionStartTimestamp <= c.endTimestamp),
        ) ?? [],
      heikin:
        candles?.heikin.filter(
          (c) =>
            c.startTimestamp >= firstTransactionStartTimestamp ||
            (c.startTimestamp <= firstTransactionStartTimestamp &&
              firstTransactionStartTimestamp <= c.endTimestamp),
        ) ?? [],
    }),
    [candles?.heikin, candles?.normal, firstTransactionStartTimestamp],
  );

  /**
   * @TODO
   * I need to filter transactions, the last complex params bundle
   * Because we can't do all of them, yet.
   *
   * We need the specific timeframes of that transaction
   * RIP
   */

  /**
   * @TODO
   * We want to bundle the transactions like this:
   * [
   *   {
   *     [startTimestamp multi candle]: [Transaction, Transaction, ...],
   *     ...
   *   }
   * ]
   *
   * We need to have the multiT candles since the real transaction start
   * And always find the startTimestamp where the transactionTimestamp is between
   * If in the main object the key already exists, push onto the array
   * else create a new one
   *
   * When we fetch our profit calc transactions,
   * the length should always be 2 if they are fully in sync
   */

  const allTransactions = useMemo(() => {
    const allTransactions = [];

    if (realCompactTransactions) {
      allTransactions.push(realCompactTransactions);
    }

    if (isCompareToProfitCalcs && compactProfitCalcsTransactions) {
      allTransactions.push(compactProfitCalcsTransactions);
    }

    return allTransactions;
  }, [
    compactProfitCalcsTransactions,
    isCompareToProfitCalcs,
    realCompactTransactions,
  ]);

  /** ============================================== FUNCTIONS ============================================== */

  const getIndividualTransactions = useCallback(
    (isProfitTransaction: boolean, compactTransactionId: string | null) => {
      const ts = isProfitTransaction
        ? profitCalcsTransactions
        : transactions?.allTransactions;

      return (
        ts?.filter((t) => t.compactTransactionId === compactTransactionId) ?? []
      );
    },
    [profitCalcsTransactions, transactions?.allTransactions],
  );

  /** ============================================== EFFECTS ============================================== */

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  return (
    <div className="detail-transactions-candles-root" ref={rootRef}>
      {transactions && rootDimensions ? (
        <CandleSticks
          dimensions={rootDimensions}
          candleResponse={filteredCandles}
          // bot={bot}
          baseAsset={bot.baseAsset}
          quoteAsset={bot.quoteAsset}
          exchange={bot.defaultExchange}
          timeFrame={timeFrame}
          candleType={CandleType.Heikin}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          // transactions={allTransactions}
          // transactionsByTimestamp={bundledTransactions}
          transactionsSetsLength={allTransactions.length}
          multiOrderCandles={multiOrderCandles}
          getIndividualTransactions={getIndividualTransactions}
          candleSticksLatestOrder={candleSticksLatestOrder}
          scale={'log'}
          chartOptionsComponent={
            <ChartOptions
              isCompareToProfitCalcs={isCompareToProfitCalcs}
              setIsCompareToProfitCalcs={setIsCompareToProfitCalcs}
            />
          }
          useIndicators={false}
        />
      ) : (
        <div className="detail-transactions-candles-root-loading-container">
          <LoadingIcons.Grid width={40} />
        </div>
      )}
    </div>
  );
};

export default TransactionCandles;
